import React from 'react';
import { Box, Typography, Button, Container } from '@mui/material';
import { ErrorOutline, Home, Refresh } from '@mui/icons-material';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <Container maxWidth="md" sx={{ py: 8 }}>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            textAlign="center"
            gap={3}
          >
            <ErrorOutline 
              sx={{ 
                fontSize: 80, 
                color: '#f59e0b',
                mb: 2 
              }} 
            />
            
            <Typography variant="h4" fontWeight="600" color="#1a1a1a" gutterBottom>
              Oops! Something went wrong
            </Typography>
            
            <Typography variant="body1" color="#6b7280" sx={{ maxWidth: 500 }}>
              We're sorry for the inconvenience. Our team has been notified and is working to fix this issue.
              Please try refreshing the page or return to the homepage.
            </Typography>

            <Box display="flex" gap={2} flexDirection={{ xs: 'column', sm: 'row' }}>
              <Button
                variant="contained"
                startIcon={<Refresh />}
                onClick={this.handleReload}
                sx={{
                  bgcolor: '#667eea',
                  '&:hover': { bgcolor: '#5a67d8' },
                  minWidth: 140
                }}
              >
                Reload Page
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<Home />}
                onClick={this.handleGoHome}
                sx={{ minWidth: 140 }}
              >
                Go Home
              </Button>
            </Box>

            {/* Development mode error details */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <Box
                sx={{
                  mt: 4,
                  p: 3,
                  bgcolor: '#fef3f2',
                  border: '1px solid #fecaca',
                  borderRadius: 2,
                  textAlign: 'left',
                  width: '100%',
                  maxWidth: 600
                }}
              >
                <Typography variant="h6" color="#dc2626" gutterBottom>
                  Error Details (Development Mode)
                </Typography>
                <Typography 
                  variant="body2" 
                  component="pre" 
                  sx={{ 
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    color: '#7f1d1d',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}
                >
                  {this.state.error.toString()}
                  {this.state.errorInfo.componentStack}
                </Typography>
              </Box>
            )}
          </Box>
        </Container>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
